import { doc, getDoc, runTransaction, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../lib/firebase';
import { canUpload, UserStorageData, FileMetadata } from '../lib/storageQuota';

interface UploadUrlResponse {
  uploadUrl: string;
  publicUrl: string;
  key: string;
  originalUrl?: string;
  originalKey?: string;
}

export class StorageQuotaError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StorageQuotaError';
  }
}

export async function checkStorageQuota(
  uid: string,
  fileSizeBytes: number
): Promise<UserStorageData> {
  const userDocRef = doc(db, 'users', uid);
  const userDoc = await getDoc(userDocRef);

  if (!userDoc.exists()) {
    throw new Error('ไม่พบข้อมูลผู้ใช้');
  }

  const userData = userDoc.data() as UserStorageData;
  userData.storageUsedBytes = userData.storageUsedBytes || 0;
  userData.addons = userData.addons || { extraStorageGB: 0, active: false };

  if (!canUpload(userData, fileSizeBytes)) {
    throw new StorageQuotaError(
      'พื้นที่จัดเก็บไม่เพียงพอ กรุณาอัปเกรดแพ็กเกจหรือซื้อ Add-on'
    );
  }

  return userData;
}

export async function getUploadUrlWithQuota(
  fileName: string,
  fileType: string,
  fileSize: number,
  file: File,
  onProgress?: (progress: number, message: string) => void
): Promise<UploadUrlResponse & { hasIssue?: boolean }> {
  const currentUser = auth.currentUser;
  
  console.log('[Upload] Starting upload process...');
  console.log('[Upload] User:', currentUser ? {
    uid: currentUser.uid,
    email: currentUser.email,
    emailVerified: currentUser.emailVerified
  } : 'NOT LOGGED IN');
  
  if (!currentUser) {
    console.error('[Upload] BLOCKED: User not authenticated');
    throw new Error('กรุณา login เข้าระบบก่อนอัปโหลดไฟล์');
  }

  console.log('[Upload] Checking storage quota...');
  const userData = await checkStorageQuota(currentUser.uid, fileSize);
  console.log('[Upload] Quota check passed:', {
    plan: userData.plan,
    storageUsed: userData.storageUsedBytes,
    fileSize: fileSize
  });

  console.log('[Upload] Getting fresh Firebase ID token...');
  const idToken = await currentUser.getIdToken(true);
  console.log('[Upload] Token obtained (length):', idToken.length);
  
  const FILE_SIZE_THRESHOLD = 100 * 1024 * 1024; // 100MB
  
  // Use chunked upload for large files
  if (fileSize > FILE_SIZE_THRESHOLD) {
    console.log('[Upload] Large file detected, using chunked upload');
    return await uploadLargeFile(file, fileName, fileType, idToken, onProgress);
  }
  
  console.log('[Upload] Uploading file to Cloudflare Worker...');
  
  // Send file as binary with metadata in headers (avoid FormData parsing issues)
  try {
    console.log('[Upload] Preparing file upload:', {
      fileName,
      fileSize: file.size,
      fileType: file.type
    });
    
    // Simulate progress for small files
    if (onProgress) {
      onProgress(10, 'กำลังเตรียมข้อมูล...');
    }
    
    // Read file as ArrayBuffer
    const fileBuffer = await file.arrayBuffer();
    
    console.log('[Upload] File read as ArrayBuffer, size:', fileBuffer.byteLength);
    console.log('[Upload] Sending request to Worker...');
    
    const response = await fetch('https://edumi-upload-api.edumi-subdomin.workers.dev/binary-upload', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': file.type || 'application/octet-stream',
        'X-File-Name': encodeURIComponent(fileName),
        'X-File-Size': file.size.toString(),
      },
      body: fileBuffer,
    });
    
    if (onProgress) {
      onProgress(90, 'กำลังประมวลผล...');
    }
    
    console.log('[Upload] Worker response status:', response.status, response.statusText);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Upload] Worker error response:', errorText);
      
      let errorMessage = 'Upload failed';
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.error || errorJson.message || errorMessage;
      } catch (e) {
        errorMessage = errorText || `HTTP ${response.status}`;
      }
      
      if (response.status === 401 || response.status === 403) {
        throw new Error('Authentication failed: กรุณา logout และ login ใหม่');
      }
      
      throw new Error(errorMessage);
    }
    
    const result = await response.json();
    console.log('[Upload] Upload success:', result);
    
    if (!result.publicUrl || !result.key) {
      console.error('[Upload] Incomplete response from worker:', result);
      throw new Error('Worker ส่งข้อมูลไม่ครบถ้วน');
    }
    
    if (onProgress) {
      onProgress(100, 'เสร็จสิ้น!');
    }
    
    return {
      uploadUrl: result.publicUrl,
      publicUrl: result.publicUrl,
      key: result.key,
      originalUrl: result.originalUrl,
      originalKey: result.originalKey,
      hasIssue: false
    };
  } catch (error: any) {
    console.error('[Upload] Upload error:', error);
    throw error;
  }
}

export async function updateStorageUsageAfterUpload(
  uid: string,
  fileData: {
    key: string;
    path: string;
    sizeBytes: number;
    mimeType: string;
    bucket: string;
    publicUrl: string;
  }
): Promise<string> {
  console.log('[Upload] Updating storage usage for user:', uid);
  console.log('[Upload] File data:', {
    key: fileData.key,
    sizeBytes: fileData.sizeBytes,
    mimeType: fileData.mimeType
  });
  
  const userDocRef = doc(db, 'users', uid);
  const filesCollectionRef = collection(db, 'files');

  let fileDocId: string = '';

  await runTransaction(db, async (transaction) => {
    const userDoc = await transaction.get(userDocRef);
    
    if (!userDoc.exists()) {
      console.error('[Upload] User document not found:', uid);
      throw new Error('ไม่พบข้อมูลผู้ใช้');
    }

    const currentUsage = userDoc.data().storageUsedBytes || 0;
    const newUsage = currentUsage + fileData.sizeBytes;

    console.log('[Upload] Storage update:', {
      before: currentUsage,
      added: fileData.sizeBytes,
      after: newUsage
    });

    transaction.update(userDocRef, {
      storageUsedBytes: newUsage,
    });

    const fileDocRef = doc(filesCollectionRef);
    fileDocId = fileDocRef.id;

    const fileMetadata: FileMetadata = {
      uid,
      key: fileData.key,
      path: fileData.path,
      sizeBytes: fileData.sizeBytes,
      mimeType: fileData.mimeType,
      createdAt: serverTimestamp(),
      status: 'active',
      bucket: fileData.bucket,
      publicUrl: fileData.publicUrl,
    };

    transaction.set(fileDocRef, fileMetadata);
  });

  console.log('[Upload] Storage usage updated successfully, fileDocId:', fileDocId);
  return fileDocId;
}

export async function deleteFileAndUpdateQuota(
  uid: string,
  fileId: string
): Promise<void> {
  const userDocRef = doc(db, 'users', uid);
  const fileDocRef = doc(db, 'files', fileId);

  await runTransaction(db, async (transaction) => {
    const fileDoc = await transaction.get(fileDocRef);
    
    if (!fileDoc.exists()) {
      throw new Error('ไม่พบไฟล์');
    }

    const fileData = fileDoc.data() as FileMetadata;
    
    if (fileData.uid !== uid) {
      throw new Error('ไม่มีสิทธิ์ลบไฟล์นี้');
    }

    if (fileData.status === 'deleted') {
      throw new Error('ไฟล์ถูกลบไปแล้ว');
    }

    const userDoc = await transaction.get(userDocRef);
    
    if (!userDoc.exists()) {
      throw new Error('ไม่พบข้อมูลผู้ใช้');
    }

    const currentUsage = userDoc.data().storageUsedBytes || 0;
    const newUsage = Math.max(0, currentUsage - fileData.sizeBytes);

    transaction.update(userDocRef, {
      storageUsedBytes: newUsage,
    });

    transaction.update(fileDocRef, {
      status: 'deleted',
    });
  });
}

// Calculate optimal chunk size based on file size
function getOptimalChunkSize(fileSize: number): number {
  const MB = 1024 * 1024;
  
  if (fileSize < 200 * MB) {
    return 10 * MB; // 10MB for files < 200MB
  } else if (fileSize < 500 * MB) {
    return 20 * MB; // 20MB for files 200-500MB
  } else if (fileSize < 1024 * MB) {
    return 30 * MB; // 30MB for files 500MB-1GB
  } else {
    return 50 * MB; // 50MB for files > 1GB
  }
}

// Upload a single chunk with retry mechanism
async function uploadChunkWithRetry(
  chunk: Blob,
  chunkNumber: number,
  uploadId: string,
  key: string,
  idToken: string,
  maxRetries: number = 3
): Promise<string> {
  let lastError: Error | null = null;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`[Upload] Uploading chunk ${chunkNumber} (attempt ${attempt}/${maxRetries})`);
      
      const chunkResponse = await fetch('https://edumi-upload-api.edumi-subdomin.workers.dev/upload-chunk', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'X-Upload-Id': uploadId,
          'X-Part-Number': String(chunkNumber),
          'X-Key': key,
        },
        body: chunk,
      });
      
      if (!chunkResponse.ok) {
        throw new Error(`Server returned ${chunkResponse.status}`);
      }
      
      const { etag } = await chunkResponse.json();
      console.log(`[Upload] Chunk ${chunkNumber} uploaded successfully`);
      return etag;
      
    } catch (error: any) {
      lastError = error;
      console.warn(`[Upload] Chunk ${chunkNumber} failed (attempt ${attempt}/${maxRetries}):`, error.message);
      
      if (attempt < maxRetries) {
        // Wait before retry (exponential backoff: 2s, 4s, 8s)
        const waitTime = Math.pow(2, attempt) * 1000;
        console.log(`[Upload] Retrying chunk ${chunkNumber} in ${waitTime}ms...`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    }
  }
  
  throw new Error(`Failed to upload chunk ${chunkNumber} after ${maxRetries} attempts: ${lastError?.message}`);
}

async function uploadLargeFile(
  file: File,
  fileName: string,
  fileType: string,
  idToken: string,
  onProgress?: (progress: number, message: string) => void
): Promise<UploadUrlResponse & { hasIssue?: boolean }> {
  console.log('[Upload] Starting chunked upload for large file');
  
  const CHUNK_SIZE = getOptimalChunkSize(file.size);
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  
  console.log('[Upload] File size:', file.size, 'bytes');
  console.log('[Upload] Chunk size:', CHUNK_SIZE / 1024 / 1024, 'MB');
  console.log('[Upload] Total chunks:', totalChunks);
  
  console.log('[Upload] File will be split into', totalChunks, 'chunks');
  
  if (onProgress) {
    onProgress(0, `กำลังอัปโหลด... (0%)`);
  }
  
  // Start multipart upload
  const startResponse = await fetch('https://edumi-upload-api.edumi-subdomin.workers.dev/start-upload', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      fileName,
      fileType,
      fileSize: file.size,
    }),
  });
  
  if (!startResponse.ok) {
    throw new Error('Failed to start chunked upload');
  }
  
  const { uploadId, key } = await startResponse.json();
  console.log('[Upload] Multipart upload started:', { uploadId, key });
  
  // Upload chunks with retry mechanism
  const parts = [];
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const chunk = file.slice(start, end);
    
    const progress = Math.round(((i + 1) / totalChunks) * 100);
    const message = `กำลังอัปโหลด... (${progress}%)`;
    
    if (onProgress) {
      onProgress(progress, message);
    }
    
    // Upload chunk with retry
    const etag = await uploadChunkWithRetry(
      chunk,
      i + 1,
      uploadId,
      key,
      idToken,
      3 // Max 3 retries
    );
    
    parts.push({ partNumber: i + 1, etag });
  }
  
  console.log('[Upload] All chunks uploaded, completing upload');
  
  if (onProgress) {
    onProgress(100, 'กำลังประมวลผลและบันทึกข้อมูล...');
  }
  
  // Complete upload
  const completeResponse = await fetch('https://edumi-upload-api.edumi-subdomin.workers.dev/complete-upload', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      uploadId,
      key,
      parts,
      fileName,
      fileType,
      fileSize: file.size,
    }),
  });
  
  if (!completeResponse.ok) {
    throw new Error('Failed to complete chunked upload');
  }
  
  const result = await completeResponse.json();
  console.log('[Upload] Chunked upload completed successfully');
  
  return {
    uploadUrl: result.publicUrl,
    publicUrl: result.publicUrl,
    key: result.key,
    originalUrl: result.originalUrl,
    originalKey: result.originalKey,
    hasIssue: false,
  };
}

export async function uploadToR2(
  uploadUrl: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<void> {
  // This function is no longer used since Worker handles upload directly
  // Kept for backward compatibility
  console.log('[Upload] uploadToR2 called but not needed - Worker handles upload');
  return Promise.resolve();
}
